import { APP_URL, CDN_URL, SITE_NAME } from '../constants';
import { SEOData } from '../types';
import { convertModelTags, findSpecValue } from '../modelTagConverter';
import { isEmptyDescription } from '../helper';
import { modelDynamicTopContent } from '@/app/[brand]/[model]/pageStaticData';

export interface WebPageSchemaProps {
    name: string;
    url: string;
    description?: string;
    keywords?: string | string[];
    image?: string;
    type?: string;
}

export interface OrganizationSchemaProps {
    name: string;
    url: string;
    logo: string;
    sameAs?: string[];
}

export const makeAbsoluteImageUrl = (value: unknown) => {
    if (!value) return undefined;

    const raw = typeof value === 'string'
        ? value
        : (value as any)?.url || (value as any)?.src || (value as any)?.image || (value as any)?.source;

    if (!raw) return undefined;

    const url = String(raw).trim();
    if (!url) return undefined;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('//')) return `https:${url}`;

    const cdn = process.env.NEXT_PUBLIC_CDN_URL || '';
    if (cdn && !url.startsWith('/images/')) {
        return `${cdn.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
    }

    return `${APP_URL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
};

const getWebPageSchemaImage = (pageData: SEOData): string | undefined => {
    const imageSource = pageData.ogImage;
    if (!imageSource) return undefined;

    if (Array.isArray(imageSource)) {
        const resolved = imageSource.map((item) => makeAbsoluteImageUrl(item)).filter(Boolean) as string[];
        return resolved.length ? resolved[0] : undefined;
    }

    if (typeof imageSource === 'object') {
        return makeAbsoluteImageUrl(imageSource);
    }

    return makeAbsoluteImageUrl(imageSource);
};

/**
 * Generates WebPage schema.org structured data
 * @param props WebPage schema properties
 * @returns JSON-LD structured data object
 */
export function generateWebPageSchema(props: WebPageSchemaProps) {
    const { name, url, description, keywords, image, type = 'WebPage' } = props;

    const id = `${url}#${type}`;

    const schema: any = {
        '@context': 'https://schema.org',
        '@type': type,
        '@id': id,
        name: name,
        url: url,
    };

    if (image) {
        schema.image = image;
    }

    if (description) {
        schema.description = htmlToSchemaText(description);
    }

    // Only add keywords if they exist (as per requirement: Remove if meta keywords not available)
    // Some sources return meta_keywords as an array rather than a comma-separated string.
    const keywordsStr = Array.isArray(keywords) ? keywords.join(", ") : keywords;
    if (keywordsStr && String(keywordsStr).trim()) {
        schema.keywords = keywordsStr;
    }

    return schema;
}

/**
 * Generates WebPage schema from SEOData object
 * @param pageData SEO data containing metadata
 * @returns JSON-LD structured data object
 */
export function generateWebPageSchemaFromSEO(pageData: SEOData) {
    const url = `${APP_URL}${pageData.canonicalURL || ''}`;
    const image = getWebPageSchemaImage(pageData);

    return generateWebPageSchema({
        name: pageData.metaTitle || SITE_NAME,
        url: url,
        image,
        description: pageData.metaDescription,
        keywords: pageData.keywords,
    });
}

export type ImageGallerySchemaImageInput = {
    src: string;
    title?: string;
    label?: string;
    alt?: string;
};

export interface ImageGallerySchemaProps {
    name: string;
    url: string;
    description?: string;
    carName?: string;
    images: ImageGallerySchemaImageInput[];
}

const toImageGalleryPageUrl = (url: string) =>
    url.startsWith('http://') || url.startsWith('https://')
        ? url.replace(/\/$/, '')
        : `${APP_URL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;

/**
 * Generates ImageGallery schema.org structured data for model image pages.
 */
export function generateImageGallerySchema(props: ImageGallerySchemaProps) {
    const { name, url, description, carName = '', images } = props;
    const pageUrl = toImageGalleryPageUrl(url);

    const imageObjects = images
        .map((image) => {
            const contentUrl = makeAbsoluteImageUrl(image.src);
            if (!contentUrl) return null;

            const text = (image.title || image.alt || `${carName} Image`).trim();

            return {
                '@type': 'ImageObject',
                contentUrl,
                name: text,
                caption: text,
                description: text,
            };
        })
        .filter(Boolean);

    const schema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'ImageGallery',
        '@id': `${pageUrl}#gallery`,
        url: pageUrl,
        name,
    };

    if (description) {
        schema.description = htmlToSchemaText(description);
    }

    if (imageObjects.length > 0) {
        schema.image = imageObjects;
    }

    return schema;
}

/**
 * Converts schema object to JSON-LD script tag string
 * @param schema Schema object
 * @returns JSON-LD string for script tag
 */
export function schemaToJsonLd(schema: any): string {
    return JSON.stringify(schema);
}

/**
 * Formats a date string to Indian Standard Time (IST) in ISO 8601 format
 * @param dateStr UTC date string
 * @returns Formatted IST date string (e.g., 2026-04-20T09:00:00+05:30)
 */
export function getNewsArticleAuthor(news: {
    author?: string;
    author_profile_id?: { username?: string; name?: string };
}): { name: string; url?: string } {
    const name = news?.author_profile_id?.name?.trim() || news?.author?.trim() || 'Car Junction';
    const username = news?.author_profile_id?.username;
    const url = username ? `${APP_URL}/authors/${username}` : undefined;
    return { name, ...(url ? { url } : {}) };
}

export function getNewsArticlePublishedTime(news: { published_at?: string }): string | undefined {
    const raw = news?.published_at;
    return raw ? formatDateIST(raw) : undefined;
}

export function formatDateIST(dateStr: string): string {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    
    // Add 5 hours and 30 minutes to UTC time
    const istOffset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(date.getTime() + istOffset);
    
    const year = istDate.getUTCFullYear();
    const month = String(istDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(istDate.getUTCDate()).padStart(2, '0');
    const hours = String(istDate.getUTCHours()).padStart(2, '0');
    const minutes = String(istDate.getUTCMinutes()).padStart(2, '0');
    const seconds = String(istDate.getUTCSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+05:30`;
}

// utils/htmlToSchemaText.ts

export function htmlToSchemaText(html: string = ""): string {
  if (!html) return "";

  return html
    // remove script/style
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")

    // headings -> line breaks
    .replace(/<\/h[1-6]>/gi, "\n")
    .replace(/<h[1-6][^>]*>/gi, "\n")

    // paragraphs & breaks
    .replace(/<\/p>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")

    // list items
    .replace(/<li[^>]*>/gi, "\n• ")
    .replace(/<\/li>/gi, "\n")

    // table rows -> new line
    .replace(/<\/tr>/gi, "\n")
    .replace(/<\/td>/gi, " | ")
    .replace(/<tr[^>]*>/gi, "")
    .replace(/<td[^>]*>/gi, "")

    // remove links but keep text
    .replace(/<a[^>]*>(.*?)<\/a>/gi, "$1")

    // remove all remaining tags
    .replace(/<[^>]+>/g, "")

    // decode entities
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")

    // clean pipes and bullets spacing
    .replace(/\s+\|\s+/g, " | ")
    .replace(/\n\s*\|\s*\n/g, "\n")
    .replace(/\n{2,}/g, "\n")

    // collapse spaces
    .replace(/\s+/g, " ")
    .replace(/\n\s+/g, "\n")
    .trim();
}

export interface NewsArticleSchemaProps {
    url: string;
    headline: string;
    description: string;
    image: {
        url: string;
        width?: number;
        height?: number;
    };
    author: {
        name: string;
        url?: string;
    };
    datePublished: string;
    dateModified?: string;
    articleSection?: string;
    keywords?: string;
    articleBody?: string;
}

/**
 * Generates NewsArticle schema.org structured data
 * @param props NewsArticle schema properties
 * @returns JSON-LD structured data object
 */
export function generateNewsArticleSchema(props: NewsArticleSchemaProps) {
    const {
        url,
        headline,
        description,
        image,
        author,
        datePublished,
        dateModified,
        articleSection,
        keywords,
        articleBody,
    } = props;

    const normalizedImageUrl = image?.url ? makeAbsoluteImageUrl(image.url) : undefined;

    const schema: any = {
        '@context': 'https://schema.org',
        '@id': `${url}#news-article`,
        '@type': 'NewsArticle',
        'inLanguage': 'en-IN',
        'mainEntityOfPage': {
            '@type': 'WebPage',
            '@id': url,
        },
        'url': url,
        'headline': headline,
        'description': htmlToSchemaText(description),
        'image': {
            '@type': 'ImageObject',
            'url': normalizedImageUrl || image.url,
            'width': image.width || 800,
            'height': image.height || 400,
        },
        'author': {
            '@type': 'Person',
            'name': author.name,
            ...(author.url ? { 'url': author.url } : {}),
        },
        'publisher': {
            '@type': 'Organization',
            'name': SITE_NAME,
            'logo': {
                '@type': 'ImageObject',
                'url': `${APP_URL}/images/car-junction-logo.svg`,
                'width': 96,
                'height': 48,
            },
        },
        'datePublished': formatDateIST(datePublished),
        'dateModified': formatDateIST(dateModified || datePublished),
        'articleSection': articleSection || 'Car News',
    };

    if (!!keywords) {
        schema.keywords = keywords;
    }

    if (articleBody) {
        schema.articleBody = htmlToSchemaText(articleBody);
    }

    return schema;
}

/**
 * Generates Organization schema.org structured data
 * @param props Organization schema properties
 * @returns JSON-LD structured data object
 */
export function generateOrganizationSchema(props: OrganizationSchemaProps) {
    const { name, url, logo, sameAs } = props;

    const schema: any = {
        '@type': 'Organization',
        '@id': `${url}#organization`,
        'name': name,
        'url': url,
        'logo': {
            '@type': 'ImageObject',
            '@id': `${url}#logo`,
            'url': logo,
        },
    };

    if (sameAs && sameAs.length > 0) {
        schema.sameAs = sameAs;
    }

    return schema;
}

export interface WebSiteSchemaProps {
    name: string;
    url: string;
    inLanguage?: string;
}

/**
 * Generates WebSite schema.org structured data. Intended for the homepage only.
 * @param props WebSite schema properties
 * @returns JSON-LD structured data object
 */
export function generateWebSiteSchema(props: WebSiteSchemaProps) {
    const { name, url, inLanguage = 'en-IN' } = props;

    return {
        '@type': 'WebSite',
        '@id': `${url}#website`,
        'url': url,
        'name': name,
        'inLanguage': inLanguage,
    };
}

/**
 * Combines multiple schemas into a single @graph schema
 * @param schemas Array of schema objects
 * @returns JSON-LD structured data object with @graph
 */
export function generateGraphSchema(schemas: any[]) {
    return {
        '@context': 'https://schema.org',
        '@graph': schemas,
    };
}

/**
 * Helper function to extract all specs from variant's specSections as PropertyValue objects
 * Uses existing findSpecValue utility for consistency
 */
function extractAdditionalProperties(variant: any): any[] {
    const properties: any[] = [];
    
    if (!variant || !Array.isArray(variant.specSections)) return properties;
    
    const importantSpecs = [
        'max_power', 'max_torque', 'arai_mileage', 'no_of_cylinders', 
        'gearbox'
    ];
    
    for (const specKey of importantSpecs) {
        const value = findSpecValue(variant, specKey);
        if (value) {
            // Extract label from spec
            let label = specKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            
            // Find original label from specSections
            for (const section of variant.specSections) {
                if (Array.isArray(section.specs)) {
                    const spec = section.specs.find((s: any) => s.key === specKey);
                    if (spec && spec.label) {
                        label = spec.label;
                        break;
                    }
                }
            }
            
            properties.push({
                '@type': 'PropertyValue',
                'name': label,
                'value': String(value),
            });
        }
    }
    
    return properties;
}

/**
 * Helper function to format image URLs for schema
 */
function formatImageUrls(images: any[], cdnUrl: string = ''): string[] {
    if (!Array.isArray(images)) return [];
    
    return images
        .filter(img => img)
        .map(img => {
            const url = typeof img === 'string' ? img : img.url || '';
            // Add CDN URL if not already present and doesn't start with http
            if (url && !url.startsWith('http')) {
                return `${cdnUrl}${url}`;
            }
            return url;
        })
        .filter(url => url); // filter out empty strings
}

export interface GenerateProductGroupCarSchemaProps {
    modelObj: any;
    brandSlug: string;
    modelSlug: string;
    variants?: any[];
    similarModels?: any[];
    userReviewsData?: {
        reviews: any[];
        averageRating: number;
        totalCount: number;
    };
    /** Latest news articles for the model, used to populate the Car entity's subjectOf */
    newsArticles?: any[];
    cdnUrl?: string;
}

/**
 * Generates a [ProductGroup, Car] schema.org structured data pair for car model pages.
 * ProductGroup represents the model as a whole (varying by configuration/fuel/transmission),
 * Car represents the base/default configuration, with each variant as its own Car under hasVariant.
 * @param props Schema configuration
 * @returns JSON-LD array [ProductGroup, Car], or null if modelObj is missing
 */
export function generateProductGroupCarSchema(props: GenerateProductGroupCarSchemaProps) {
    const {
        modelObj,
        brandSlug,
        modelSlug,
        variants = [],
        similarModels = [],
        userReviewsData,
        newsArticles = [],
        cdnUrl = '',
    } = props;

    if (!modelObj) return null;

    // Basic information
    const carName = `${modelObj.brand_name || ''} ${modelObj.name || ''}`.trim();
    const url = `${APP_URL}/${brandSlug}/${modelSlug}`;
    const productGroupId = `${url}#productgroup`;
    const carId = `${url}#car`;
    const productGroupID = modelObj.detailed_slug || `${brandSlug}-${modelSlug}`;

    // Price information
    const priceBase = Number(modelObj.price_range?.base ?? 0) || 0;
    const priceTop = Number(modelObj.price_range?.top ?? 0) || 0;
    
    // Base variant specs - use existing findSpecValue utility.
    // modelObj.base_variant is never populated by /model/detail — derive from
    // the variants list, falling back to modelObj itself (which carries
    // specSections directly, see ModelRepository.findDetailWithLookups).
    const baseVariant =
        variants.find((v: any) => v.is_base_variant) ?? variants[0] ?? modelObj;

    // Extract individual specifications using existing utility
    const engineDisplacement = baseVariant ? findSpecValue(baseVariant, 'engine_displacement') : '';
    const maxPower = baseVariant ? findSpecValue(baseVariant, 'max_power') : '';
    const maxTorque = baseVariant ? findSpecValue(baseVariant, 'max_torque') : '';
    const wheelbase = baseVariant ? findSpecValue(baseVariant, 'wheel_base') : '';
    const bootSpace = baseVariant ? findSpecValue(baseVariant, 'boot_space') : '';
    const gearbox = baseVariant ? findSpecValue(baseVariant, 'gearbox') : '';
    const noCylinders = baseVariant ? findSpecValue(baseVariant, 'no_of_cylinders') : '';
    const noOfDoors = baseVariant ? findSpecValue(baseVariant, 'no_of_doors') : '';
    const wheelDrive = baseVariant ? findSpecValue(baseVariant, 'wheel_drive') : '';
    const colors = baseVariant ? findSpecValue(baseVariant, 'colors') : '';

    const isElectric = Array.isArray(modelObj?.fuel_types) ? modelObj.fuel_types.some((type: string) => type?.toLowerCase() === 'electric') : false;
    const isDiscontinued = modelObj?.status === 'discontinued';

    // Description (shared by ProductGroup and Car). For discontinued models,
    // make sure the notice is present without duplicating it if the CMS
    // content already mentions it.
    const rawDescription = modelObj?.top_description || modelObj?.description || "";
    const description = isEmptyDescription(rawDescription)
        ? convertModelTags(isElectric ? modelDynamicTopContent.EV : modelDynamicTopContent.nonEV, modelObj)
        : convertModelTags(rawDescription, modelObj);
    const schemaDescription = description
        ? (() => {
            const text = htmlToSchemaText(description);
            if (isDiscontinued && !/discontinued/i.test(text)) {
                return `${text}${/[.!?]$/.test(text) ? '' : '.'} This model has been discontinued in India.`;
            }
            return text;
        })()
        : undefined;

    // Offer fields that differ for a discontinued model
    const offerAvailability = isDiscontinued ? 'https://schema.org/Discontinued' : 'https://schema.org/InStock';
    const priceSpecName = isDiscontinued ? 'Last known ex-showroom price' : 'Ex-showroom price';

    const brandBlock = modelObj.brand_name ? { '@type': 'Brand', 'name': modelObj.brand_name } : undefined;
    const manufacturerBlock = (modelObj.brand?.name || modelObj.brand_name)
        ? { '@type': 'Organization', 'name': modelObj.brand?.name || modelObj.brand_name }
        : undefined;

    const slugify = (v: any) => String(v ?? '').toLowerCase().replace(/\s+/g, '-');

    // Caps mirror what's actually rendered on the model page
    const MAX_REVIEWS = 5;
    const MAX_SIMILAR_CARS = 10;
    const MAX_NEWS_ARTICLES = 10;

    // ── ProductGroup ─────────────────────────────────────────────────────
    const productGroup: any = {
        '@context': 'https://schema.org',
        '@type': 'ProductGroup',
        '@id': productGroupId,
        'name': carName,
        'url': url,
        ...(schemaDescription && { 'description': schemaDescription }),
        ...(brandBlock && { 'brand': brandBlock }),
        ...(manufacturerBlock && { 'manufacturer': manufacturerBlock }),
        'productGroupID': productGroupID,
        'variesBy': ['vehicleConfiguration', 'fuelType', 'vehicleTransmission'],
    };

    // Ratings and reviews live on the ProductGroup
    if (userReviewsData && (userReviewsData.averageRating > 0 || userReviewsData.totalCount > 0)) {
        const avgRating = Math.min(5, Math.max(0, userReviewsData.averageRating));
        const aggregateRating: any = {
            '@type': 'AggregateRating',
            'ratingValue': avgRating > 0 ? parseFloat(avgRating.toFixed(1)) : undefined,
            'reviewCount': userReviewsData.totalCount || undefined,
            'bestRating': 5,
            'worstRating': 1,
        };
        Object.keys(aggregateRating).forEach(
            key => aggregateRating[key] === undefined && delete aggregateRating[key]
        );
        productGroup.aggregateRating = aggregateRating;

        if (Array.isArray(userReviewsData.reviews) && userReviewsData.reviews.length > 0) {
            productGroup.review = userReviewsData.reviews
                .slice(0, MAX_REVIEWS)
                .map(review => ({
                    '@type': 'Review',
                    'author': {
                        '@type': 'Person',
                        'name': review.reviewer_name || 'Anonymous',
                    },
                    'datePublished': review.published_at ? formatDateIST(review.published_at) : undefined,
                    'reviewBody': review.feedback || review.review_title || '',
                    'reviewRating': {
                        '@type': 'Rating',
                        'ratingValue': Number(review.rating) || 0,
                        'bestRating': 5,
                        'worstRating': 1,
                    },
                }))
                .map((r: any) => {
                    Object.keys(r).forEach(key => r[key] === undefined && delete r[key]);
                    return r;
                });
        }
    }

    // hasVariant — one Car entry per real, sanity-checked variant returned by the API.
    // Discontinued models skip the sanity-done requirement: sanity checks aren't
    // kept up to date post-discontinuation, so enforcing it would drop most of
    // an otherwise-valid historical variant lineup down to just one or two.
    if (Array.isArray(variants) && variants.length > 0) {
        const hasVariant = variants
            .filter(v => v && v.name && v.price && (isDiscontinued || v.is_sanity_done))
            .map(variant => {
                const variantSlug = variant.slug || slugify(variant.name);
                const variantUrl = `${url}/${variantSlug}`;
                const fuelType = variant.fuel_types?.[0] || variant.fuel_type;
                const transmission = variant.transmission || variant.transmissions?.[0];

                return {
                    '@type': 'Car',
                    '@id': `${variantUrl}#car`,
                    'name': `${carName} ${variant.name}`.trim(),
                    'url': variantUrl,
                    'vehicleConfiguration': variant.name,
                    ...(fuelType && { 'fuelType': fuelType }),
                    ...(transmission && { 'vehicleTransmission': transmission }),
                    'isVariantOf': { '@id': productGroupId },
                    'offers': {
                        '@type': 'Offer',
                        'url': variantUrl,
                        'priceCurrency': 'INR',
                        'price': Number(variant.price),
                        'availability': offerAvailability,
                        ...(!isDiscontinued && { 'itemCondition': 'https://schema.org/NewCondition' }),
                        'eligibleRegion': {
                            '@type': 'Country',
                            'name': 'India',
                        },
                        'priceSpecification': {
                            '@type': 'PriceSpecification',
                            'name': priceSpecName,
                            'priceCurrency': 'INR',
                            'price': Number(variant.price),
                        },
                    },
                };
            });

        if (hasVariant.length > 0) {
            productGroup.hasVariant = hasVariant;
        }
    }

    Object.keys(productGroup).forEach(key => {
        if (productGroup[key] === undefined || (typeof productGroup[key] === 'object' && !Array.isArray(productGroup[key]) && Object.keys(productGroup[key]).length === 0)) {
            delete productGroup[key];
        }
    });

    // ── Car (base/default configuration) ────────────────────────────────
    const car: any = {
        '@context': 'https://schema.org',
        '@type': 'Car',
        '@id': carId,
        'name': carName,
        'url': url,
    };

    // Add images
    const imageUrls = formatImageUrls(modelObj.images, cdnUrl);
    if (imageUrls.length > 0) {
        car.image = imageUrls;
    }

    if (schemaDescription) {
        car.description = schemaDescription;
    }

    if (brandBlock) car.brand = brandBlock;
    if (manufacturerBlock) car.manufacturer = manufacturerBlock;

    // Add model name
    if (modelObj.name) {
        car.model = modelObj.name;
    }

    // Add body type and category
    if (modelObj.body_types && Array.isArray(modelObj.body_types) && modelObj.body_types.length > 0) {
        const bodyType = modelObj.body_types[0].name || '';
        if (bodyType) {
            car.category = bodyType;
            car.bodyType = bodyType;
        }
    }

    // Add fuel types
    if (modelObj.fuel_types && Array.isArray(modelObj.fuel_types) && modelObj.fuel_types.length > 0) {
        car.fuelType = modelObj.fuel_types;
    }

    // Add transmissions
    if (modelObj.transmissions && Array.isArray(modelObj.transmissions) && modelObj.transmissions.length > 0) {
        car.vehicleTransmission = modelObj.transmissions;
    }

    // Add drivetrain configuration (only when detected from specs)
    if (wheelDrive === 'FWD') {
        car.driveWheelConfiguration = 'https://schema.org/FrontWheelDriveConfiguration';
    } else if (wheelDrive === 'RWD') {
        car.driveWheelConfiguration = 'https://schema.org/RearWheelDriveConfiguration';
    } else if (wheelDrive === 'AWD') {
        car.driveWheelConfiguration = 'https://schema.org/AllWheelDriveConfiguration';
    } else if (wheelDrive === '4WD') {
        car.driveWheelConfiguration = 'https://schema.org/FourWheelDriveConfiguration';
    }

    // Add number of doors
    if (noOfDoors) {
        const doorsValue = String(noOfDoors).replace(/\D/g, '');
        if (doorsValue) car.numberOfDoors = Number(doorsValue);
    }

    // Add seating capacity
    if (modelObj.seating_capacity) {
        const seatingValue = Array.isArray(modelObj.seating_capacity)
            ? modelObj.seating_capacity[0]
            : modelObj.seating_capacity;
        if (seatingValue) {
            car.seatingCapacity = Number(seatingValue);
        }
    }

    // Add SKU / productID
    car.sku = productGroupID;
    if (modelObj._id) {
        car.productID = modelObj._id;
    }

    // Add engine specification
    if (engineDisplacement) {
        const displacement = String(engineDisplacement).replace(/\D/g, '');
        if (displacement) {
            car.vehicleEngine = {
                '@type': 'EngineSpecification',
                'engineDisplacement': {
                    '@type': 'QuantitativeValue',
                    'value': Number(displacement),
                    'unitCode': 'CMQ', // cubic centimeter
                },
            };
        }
    }

    // Add wheelbase
    if (wheelbase) {
        const wheelbaseValue = String(wheelbase).replace(/\D/g, '');
        if (wheelbaseValue) {
            car.wheelbase = {
                '@type': 'QuantitativeValue',
                'value': Number(wheelbaseValue),
                'unitCode': 'MMT', // millimeter
            };
        }
    }

    // Add cargo volume (boot space)
    if (bootSpace) {
        const cargoValue = String(bootSpace).replace(/\D/g, '');
        if (cargoValue) {
            car.cargoVolume = {
                '@type': 'QuantitativeValue',
                'value': Number(cargoValue),
                'unitCode': 'LTR', // liters
            };
        }
    }

    // Add colors
    if (colors.length > 0) {
        car.color = colors;
    }

    // Link back to the ProductGroup
    car.isVariantOf = { '@id': productGroupId };

    // Add additional properties using base variant
    if (baseVariant) {
        const additionalProps = extractAdditionalProperties(baseVariant);
        if (isDiscontinued) {
            additionalProps.unshift({ '@type': 'PropertyValue', 'name': 'Model Status', 'value': 'Discontinued' });
        }
        if (additionalProps.length > 0) {
            car.additionalProperty = additionalProps;
        }
    }

    // Add offers information
    if (priceBase > 0 || priceTop > 0) {
        car.offers = {
            '@type': 'AggregateOffer',
            'url': url,
            'priceCurrency': 'INR',
            ...(priceBase > 0 && { 'lowPrice': priceBase }),
            ...(priceTop > 0 && { 'highPrice': priceTop }),
            ...((modelObj.variant_count || variants.length) && { 'offerCount': modelObj.variant_count || variants.length }),
            'availability': offerAvailability,
            ...(!isDiscontinued && { 'itemCondition': 'https://schema.org/NewCondition' }),
            'seller': {
                '@type': 'Organization',
                'name': SITE_NAME,
            },
            'eligibleRegion': {
                '@type': 'Country',
                'name': 'India',
            },
            'priceSpecification': {
                '@type': 'PriceSpecification',
                'name': priceSpecName,
                'priceCurrency': 'INR',
            },
        };
    }

    // Add similar models — expects the getSimilarModels() shape (same data fed
    // to the "Compare {model} Alternatives" slider / SimilarCarsSlider), i.e.
    // { brand: { name, slug }, model: { name, slug, detailed_slug } }.
    if (Array.isArray(similarModels) && similarModels.length > 0) {
        car.isSimilarTo = similarModels
            .filter(m => m && m?.model)
            .slice(0, MAX_SIMILAR_CARS)
            .map(s => ({
                '@type': 'Car',
                'name': `${s.brand?.name || ''} ${s.model?.name || ''}`.trim(),
                'url': `${APP_URL}/${s.brand?.slug || s.brand?.name?.toLowerCase()}/${s.model?.slug || slugify(s.model?.name)}`,
            }));
    }

    // Add related news articles
    if (Array.isArray(newsArticles) && newsArticles.length > 0) {
        car.subjectOf = newsArticles
            .filter(a => a && a.title && a.slug)
            .slice(0, MAX_NEWS_ARTICLES)
            .map(article => ({
                '@type': 'NewsArticle',
                'headline': article.title,
                'url': `${APP_URL}/news/${article.slug}`,
                ...(article.published_at && { 'datePublished': formatDateIST(article.published_at) }),
            }));
    }

    // Remove empty objects and undefined values
    Object.keys(car).forEach(key => {
        if (car[key] === undefined || (typeof car[key] === 'object' && !Array.isArray(car[key]) && Object.keys(car[key]).length === 0)) {
            delete car[key];
        }
    });

    return [productGroup, car];
}
export interface CarItemListGraphSchemaProps {
    id: string;
    name: string;
    url: string;
    itemListOrder: 'https://schema.org/ItemListOrderAscending' | 'https://schema.org/ItemListOrderDescending';
    cars: any[];
}

/**
 * Generates ItemList schema.org structured data for Car models to be used in a Schema Graph
 * @param props CarItemListGraph schema properties
 * @returns JSON-LD structured data object
 */
export function generateCarItemListGraphSchema(props: CarItemListGraphSchemaProps) {
    const { id, name, url, itemListOrder, cars } = props;

    return {
        '@type': 'ItemList',
        '@id': id,
        name: name,
        url: url,
        itemListOrder: itemListOrder,
        itemListElement: cars.map((car, index) => {
            const carUrl = `${APP_URL}/${car.brand_slug}/${car.slug}`;
            return {
                '@type': 'ListItem',
                position: index + 1,
                item: {
                    '@type': 'Car',
                    '@id': `${carUrl}#car`,
                    name: `${car.brand_name} ${car.name}`,
                    url: carUrl,
                    // Use first available image field (featured_image, image, images[0].url, images[0])
                    image: (() => {
                        const img = car.featured_image || car.image || car.images?.[0]?.url || car.images?.[0] || "";
                        return img ? `${process.env.NEXT_PUBLIC_CDN_URL}${img}` : "";
                    })(),
                },
            };
        }),
    };
}

export interface CarItemListSchemaProps {
    name: string;
    cars: any[];
}

/**
 * Generates ItemList schema.org structured data for Car models
 * @param props CarItemList schema properties
 * @returns JSON-LD structured data object
 */
export function generateCarItemListSchema(props: CarItemListSchemaProps) {
    const { name, cars } = props;

    return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: name,
        itemListElement: cars.map((car, index) => {
            const carUrl = `${APP_URL}/${car.brand_slug}/${car.slug}`;
            return {
                '@type': 'ListItem',
                position: index + 1,
                item: {
                    '@type': 'Car',
                    name: `${car.brand_name} ${car.name}`,
                    url: carUrl,
                    // Use first available image field (featured_image, image, images[0].url, images[0])
                    image: (() => {
                        const img = car.featured_image || car.image || car.images?.[0]?.url || car.images?.[0] || "";
                        return img ? `${process.env.NEXT_PUBLIC_CDN_URL}${img}` : "";
                    })(),
                    brand: {
                        '@type': 'Brand',
                        name: car.brand_name || '',
                    },
                },
            };
        }),
    };
}

export interface SiteNavigationItem {
    id: string;
    name: string;
    url: string;
}

type SiteNavigationSchemaProps = {
    items?: {
        id?: string;
        name: string;
        url: string;
    }[];
    pagePath?: string; // current page path
};

/**
 * Generates SiteNavigationElement graph schema
 * @param props Navigation items
 * @returns JSON-LD structured data object
 */
export function generateSiteNavigationSchema(
    props?: SiteNavigationSchemaProps
) {

    const COMMON_SITE_NAVIGATION = [
            {
                id: "nav-home",
                name: "Home",
                url: "/",
            },
            {
                id: "nav-new-cars",
                name: "New Cars",
                url: "/new-cars",
            },
            {
                id: "nav-latest-cars",
                name: "Latest Cars",
                url: "/latest-cars",
            },
            {
                id: "nav-emi-calculator",
                name: "EMI Calculator",
                url: "/car-loan-emi-calculator",
            },
            {
                id: "nav-popular-cars",
                name: "Popular Cars",
                url: "/popular-cars",
            },
            {
                id: "nav-electric-cars",
                name: "Electric Cars",
                url: "/electric-cars",
            },
            {
                id: "nav-upcoming-cars",
                name: "Upcoming Cars",
                url: "/upcoming-cars",
            },
            {
                id: "nav-compare-cars",
                name: "Compare Cars",
                url: "/compare-cars",
            },
            {
                id: "nav-brands",
                name: "Brands",
                url: "/brands",
            },
            {
                id: "nav-news",
                name: "News",
                url: "/news",
            },
            {
                id: "nav-web-stories",
                name: "Web Stories",
                url: "/web-stories",
            },
    ];

    const items  = !!props?.items?.length  ? props?.items : COMMON_SITE_NAVIGATION;

    const baseUrl = APP_URL.replace(/\/$/, "");
    const pagePath = props?.pagePath || "";

   return {
        "@context": "https://schema.org",
        "@graph": items.map((item) => ({
            "@type": "SiteNavigationElement",
            "@id": `${baseUrl}${pagePath || item.url}${item.id ? `#${item.id}` : ''}`,
            name: item.name,
            url: `${baseUrl}${item.url}`,
        })),
    };
}

export interface GenerateVariantCarSchemaProps {
    /** Full model object (from getModelByBrandAndSlug) */
    modelObj: any;
    /** Full variant object (from getVariant) */
    variantData: any;
    brandSlug: string;
    modelSlug: string;
    variantSlug: string;
    userReviewsData?: {
        reviews: any[];
        averageRating: number;
        totalCount: number;
    };
    /** Top/intro description for the variant (may be HTML) */
    topDescription?: string;
    cdnUrl?: string;
}

/**
 * Generates a comprehensive Car schema.org structured data object for variant pages.
 *
 * Reuses: extractAdditionalProperties, formatImageUrls, htmlToSchemaText,
 *         findSpecValue, formatDateIST (all defined in this file).
 *
 * @param props Variant schema configuration
 * @returns JSON-LD structured data object
 */
export function generateVariantCarSchema(props: GenerateVariantCarSchemaProps) {
    const {
        modelObj,
        variantData,
        brandSlug,
        modelSlug,
        variantSlug,
        userReviewsData,
        topDescription,
        cdnUrl = '',
    } = props;

    if (!variantData || !modelObj) return null;

    // ── URLs ────────────────────────────────────────────────────────────────
    const modelUrl    = `${APP_URL}/${brandSlug}/${modelSlug}`;
    const variantUrl  = `${modelUrl}/${variantSlug}`;
    const variantId   = `${variantUrl}#car`;

    // ── Names ───────────────────────────────────────────────────────────────
    const brandName   = modelObj.brand_name || modelObj.brand?.name || '';
    const modelName   = modelObj.name || '';
    const variantName = variantData.name || '';
    const carName     = `${brandName} ${modelName} ${variantName}`.trim();

    // ── Images ──────────────────────────────────────────────────────────────
    // Prefer variant images, fall back to model images
    const rawImages  = variantData.images?.length ? variantData.images : (modelObj.images || []);
    const imageUrls  = formatImageUrls(rawImages, cdnUrl);

    // ── Specs from variant's specSections ───────────────────────────────────
    const engineDisplacement = findSpecValue(variantData, 'engine_displacement');
    const wheelbase = findSpecValue(variantData, 'wheel_base');
    const bootSpace = findSpecValue(variantData, 'boot_space');
    const noOfDoors = findSpecValue(variantData, 'no_of_doors');
    const wheelDrive = findSpecValue(variantData, 'wheel_drive');
    const fuelType = variantData.fuel_type || (Array.isArray(variantData.fuel_types) ? variantData.fuel_types[0] : '') || '';
    const transmission = (Array.isArray(variantData.transmissions) ? variantData.transmissions[0] : '') || '';
    
    // ── Build schema object ─────────────────────────────────────────────────
    const schema: any = {
        '@context': 'https://schema.org',
        '@type':    'Car',
        '@id':      variantId,
        'name':     carName,
        'url':      variantUrl,
    };

    // Images
    if (imageUrls.length > 0) {
        schema.image = imageUrls.length === 1 ? imageUrls[0] : imageUrls;
    }

    // Description – prefer resolved topDescription, fall back to variant field
    const descriptionSource = topDescription || variantData.top_description || variantData.description || '';
    if (descriptionSource) {
        schema.description = htmlToSchemaText(descriptionSource);
    }

    // Brand
    if (brandName) {
        schema.brand = { '@type': 'Brand', 'name': brandName };
    }

    // Manufacturer
    if (brandName) {
        schema.manufacturer = { '@type': 'Organization', 'name': brandName };
    }

    // Model name
    if (modelName) {
        schema.model = modelName;
    }

    // Variant (trim/configuration)
    if (variantName) {
        schema.vehicleConfiguration = variantName;
    }

    // SKU  e.g. "mahindra-bolero-b4"
    schema.sku = `${modelObj.detailed_slug || `${brandSlug}-${modelSlug}`}-${variantSlug}`;

    // Body type / category
    if (Array.isArray(modelObj.body_types) && modelObj.body_types.length > 0) {
        const bodyType = modelObj.body_types[0]?.name || '';
        if (bodyType) {
            schema.category = bodyType;
            schema.bodyType  = bodyType;
        }
    }

    // Fuel type
    if (fuelType) {
        schema.fuelType = fuelType;
    }

    // Transmission
    if (transmission) {
        schema.vehicleTransmission = transmission.trim();
    }

    // Drive wheel configuration
    if (wheelDrive === 'FWD') {
        schema.driveWheelConfiguration = 'https://schema.org/FrontWheelDriveConfiguration';
    } else if (wheelDrive === 'RWD') {
        schema.driveWheelConfiguration = 'https://schema.org/RearWheelDriveConfiguration';
    } else if (wheelDrive === 'AWD') {
        schema.driveWheelConfiguration = 'https://schema.org/AllWheelDriveConfiguration';
    } else if (wheelDrive === '4WD') {
        schema.driveWheelConfiguration = 'https://schema.org/FourWheelDriveConfiguration';
    }

    // Number of doors
    if (noOfDoors) {
        const doorsValue = String(noOfDoors).replace(/\D/g, '');
        if (doorsValue) schema.numberOfDoors = Number(doorsValue);
    }

    // Seating capacity – prefer variant spec, then model array, then model scalar
    const seatingSpecValue = findSpecValue(variantData, 'seating_capacity');
    const seatingCapacity  = seatingSpecValue
        || (Array.isArray(variantData.seating_capacity) ? variantData.seating_capacity[0] : variantData.seating_capacity)
        || (Array.isArray(modelObj.seating_capacity)    ? modelObj.seating_capacity[0]    : modelObj.seating_capacity);
    if (seatingCapacity) {
        schema.seatingCapacity = Number(String(seatingCapacity).replace(/\D/g, '')) || undefined;
    }

    // Engine specification
    if (engineDisplacement) {
        const displacement = String(engineDisplacement).replace(/\D/g, '');
        if (displacement) {
            schema.vehicleEngine = {
                '@type': 'EngineSpecification',
                'engineDisplacement': {
                    '@type':    'QuantitativeValue',
                    'value':    Number(displacement),
                    'unitCode': 'CMQ', // cubic centimetres
                },
                ...(fuelType && { 'fuelType': fuelType }),
            };
        }
    }

    // Wheelbase
    if (wheelbase) {
        const wb = String(wheelbase).replace(/\D/g, '');
        if (wb) {
            schema.wheelbase = {
                '@type':    'QuantitativeValue',
                'value':    Number(wb),
                'unitCode': 'MMT', // millimetres
            };
        }
    }

    // Cargo / boot volume
    if (bootSpace) {
        const cargo = String(bootSpace).replace(/\D/g, '');
        if (cargo) {
            schema.cargoVolume = {
                '@type':    'QuantitativeValue',
                'value':    Number(cargo),
                'unitCode': 'LTR', // litres
            };
        }
    }

    // Additional properties (power, torque, mileage, gearbox, cylinders …)
    const additionalProps = extractAdditionalProperties(variantData);
    if (additionalProps.length > 0) {
        schema.additionalProperty = additionalProps;
    }

    // Offer – single price for this variant
    const price = variantData.price || variantData.variant_price;
    if (price && Number(price) > 0) {
        const currentYear = new Date().getFullYear();
        schema.offers = {
            '@type':          'Offer',
            'url':            variantUrl,
            'priceCurrency':  'INR',
            'price':          Number(price),
            'availability':   'https://schema.org/InStock',
            'itemCondition':  'https://schema.org/NewCondition',
            // 'priceValidUntil': `${currentYear}-12-31`,
            'eligibleRegion': {
                '@type': 'Country',
                'name':  'India',
            },
            'seller': {
                '@type': 'Organization',
                'name':  SITE_NAME,
            },
            'priceSpecification': {
                '@type':         'PriceSpecification',
                'name':          'Ex-showroom price',
                'priceCurrency': 'INR',
                'price':         Number(price),
            },
        };
    }

    // isVariantOf – link back to the parent model Car entity
    schema.isVariantOf = {
        '@type': 'Car',
        '@id':   `${modelUrl}#car`,
        'name':  `${brandName} ${modelName}`.trim(),
        'url':   modelUrl,
    };

    // Aggregate rating + individual reviews
    if (userReviewsData && (userReviewsData.averageRating > 0 || userReviewsData.totalCount > 0)) {
        const avgRating = Math.min(5, Math.max(0, userReviewsData.averageRating));
        schema.aggregateRating = {
            '@type':       'AggregateRating',
            'ratingValue': avgRating > 0 ? parseFloat(avgRating.toFixed(1)) : undefined,
            'reviewCount': userReviewsData.totalCount || undefined,
            'bestRating':  5,
            'worstRating': 1,
        };
        // Remove undefined values
        Object.keys(schema.aggregateRating).forEach(
            key => schema.aggregateRating[key] === undefined && delete schema.aggregateRating[key]
        );

        if (Array.isArray(userReviewsData.reviews) && userReviewsData.reviews.length > 0) {
            schema.review = userReviewsData.reviews
                .map(review => ({
                    '@type': 'Review',
                    'author': {
                        '@type': 'Person',
                        'name':  review.reviewer_name || 'Anonymous',
                    },
                    'datePublished': review.published_at ? formatDateIST(review.published_at) : undefined,
                    'name': review.review_title || '',
                    'reviewBody': review.feedback || review.review_title || '',
                    'reviewRating': {
                        '@type':       'Rating',
                        'ratingValue': Number(review.rating) || 0,
                        'bestRating':  5,
                        'worstRating': 1,
                    },
                }))
                .map((r: any) => {
                    Object.keys(r).forEach(key => r[key] === undefined && delete r[key]);
                    return r;
                });
        }
    }

    // Strip top-level keys that are undefined or empty objects
    Object.keys(schema).forEach(key => {
        if (
            schema[key] === undefined ||
            (typeof schema[key] === 'object' && !Array.isArray(schema[key]) && Object.keys(schema[key]).length === 0)
        ) {
            delete schema[key];
        }
    });

    return schema;
}

export interface GenerateModelSubPageCarSchemaProps {
    brandSlug: string;
    modelSlug: string;
    /** Display name of the page entity, e.g. "{Brand} {Model} Images" */
    name: string;
    /** Path of the sub page relative to the model URL, e.g. "images" */
    subPath: string;
    brandName: string;
    description?: string;
    images?: unknown[];
    colors?: string[];
    additionalProperty?: { name: string; value: string | number }[];
    ratingValue?: number;
    reviewCount?: number;
}

/**
 * Generates a Car schema for model sub pages (images, colours, EMI calculator, etc.).
 * The @id points to the parent model Car entity so all sub pages describe the same car.
 * aggregateRating is emitted only when both a rating and a review count are available.
 */
export function generateModelSubPageCarSchema(props: GenerateModelSubPageCarSchemaProps) {
    const {
        brandSlug,
        modelSlug,
        name,
        subPath,
        brandName,
        description,
        images = [],
        colors = [],
        additionalProperty = [],
        ratingValue,
        reviewCount,
    } = props;

    const modelUrl = `${APP_URL}/${brandSlug}/${modelSlug}`;
    const imageUrls = Array.from(
        new Set(images.map((image) => makeAbsoluteImageUrl(image)).filter(Boolean) as string[])
    );
    const colorNames = Array.from(new Set(colors.map((color) => color?.trim()).filter(Boolean)));
    const rating = Math.min(5, Math.max(0, Number(ratingValue) || 0));
    const count = Number(reviewCount) || 0;

    const schema: any = {
        '@context': 'https://schema.org',
        '@type': 'Car',
        '@id': `${modelUrl}#car`,
        'name': name,
        'url': `${modelUrl}/${subPath}`,
        ...(imageUrls.length > 0 && { 'image': imageUrls }),
        ...(description && { 'description': htmlToSchemaText(description) }),
        'brand': { '@type': 'Brand', 'name': brandName },
        ...(colorNames.length > 0 && { 'color': colorNames }),
        ...(additionalProperty.length > 0 && {
            'additionalProperty': additionalProperty.map((property) => ({
                '@type': 'PropertyValue',
                'name': property.name,
                'value': property.value,
            })),
        }),
        ...(rating > 0 && count > 0 && {
            'aggregateRating': {
                '@type': 'AggregateRating',
                'ratingValue': parseFloat(rating.toFixed(1)),
                'reviewCount': count,
                'bestRating': 5,
                'worstRating': 1,
            },
        }),
    };

    return schema;
}
